import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { BankAccountInfo, CampaignSummary, DonorSummary, Paginated } from '@foundation/shared';
import { DONATION_CHANNELS, DONATION_CHANNEL_LABEL_TH } from '@foundation/shared';
import { ApiRequestError, api, buildQuery } from '../lib/api';
import { ErrorAlert } from '../components/ui';

/** บันทึกการบริจาคด้วยมือ — ใช้กับเงินสด เช็ค หรือสลิปที่ได้มาทางอื่น */
export default function DonationCreate() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<CampaignSummary[]>([]);
  const [banks, setBanks] = useState<BankAccountInfo[]>([]);
  const [donorResults, setDonorResults] = useState<DonorSummary[]>([]);
  const [donorQuery, setDonorQuery] = useState('');
  const [donor, setDonor] = useState<DonorSummary | null>(null);

  const [form, setForm] = useState({
    amountBaht: '',
    donatedAt: new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 16),
    channel: 'bank_transfer',
    bankAccountId: '',
    campaignId: '',
    slipRef: '',
    payerName: '',
    note: '',
  });
  const [error, setError] = useState<unknown>(null);
  const [duplicate, setDuplicate] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api<{ data: CampaignSummary[] }>('/campaigns').then((r) => setCampaigns(r.data)).catch(() => undefined);
    api<{ data: BankAccountInfo[] }>('/campaigns/meta/bank-accounts')
      .then((r) => {
        setBanks(r.data);
        const def = r.data.find((b) => b.isDefault);
        if (def) setForm((f) => ({ ...f, bankAccountId: def.id }));
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    if (donorQuery.trim().length < 2) {
      setDonorResults([]);
      return;
    }
    const ac = new AbortController();
    const t = setTimeout(() => {
      api<Paginated<DonorSummary>>(`/donors${buildQuery({ q: donorQuery, limit: 8 })}`, {
        signal: ac.signal,
      })
        .then((r) => setDonorResults(r.data))
        .catch(() => setDonorResults([]));
    }, 300);
    return () => {
      clearTimeout(t);
      ac.abort();
    };
  }, [donorQuery]);

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setDuplicate(null);

    const satang = Math.round(Number(form.amountBaht.replace(/[^\d.]/g, '')) * 100);
    if (!Number.isFinite(satang) || satang <= 0) {
      setError(new Error('กรุณาระบุจำนวนเงินให้ถูกต้อง'));
      return;
    }

    setBusy(true);
    try {
      const res = await api<{ id: string }>('/donations', {
        method: 'POST',
        idempotent: true,
        body: {
          amount: satang,
          donatedAt: new Date(`${form.donatedAt}:00+07:00`).toISOString(),
          channel: form.channel,
          bankAccountId: form.bankAccountId || undefined,
          campaignId: form.campaignId || undefined,
          donorId: donor?.id,
          slipRef: form.slipRef.trim() || undefined,
          payerName: form.payerName.trim() || undefined,
          note: form.note.trim() || undefined,
        },
      });
      navigate(`/donations/${res.id}`);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        const details = err.details as { existingDonationNo?: string } | undefined;
        if (details?.existingDonationNo) {
          setDuplicate(details.existingDonationNo);
        } else {
          setError(err);
        }
      } else {
        setError(err);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>บันทึกการบริจาค</h1>
          <p>รายการที่บันทึกจะอยู่ในสถานะรอตรวจสอบ จนกว่าจะกดยืนยันการรับเงิน</p>
        </div>
        <Link className="btn secondary" to="/donations">
          ← กลับรายการ
        </Link>
      </div>

      <ErrorAlert error={error} />
      {duplicate && (
        <div className="alert warn">
          รายการนี้ซ้ำกับ <strong>{duplicate}</strong> ที่บันทึกไว้แล้ว
          — ตรวจสอบก่อนบันทึกซ้ำ
        </div>
      )}

      <form onSubmit={submit}>
        <div className="card">
          <h2>รายละเอียดการรับเงิน</h2>
          <div className="form-row">
            <div className="field">
              <label htmlFor="amount">จำนวนเงิน (บาท) *</label>
              <input
                id="amount"
                inputMode="decimal"
                required
                value={form.amountBaht}
                onChange={(e) => set('amountBaht', e.target.value)}
              />
            </div>
            <div className="field">
              <label htmlFor="at">วันเวลาที่รับเงิน *</label>
              <input
                id="at"
                type="datetime-local"
                required
                value={form.donatedAt}
                onChange={(e) => set('donatedAt', e.target.value)}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="field">
              <label htmlFor="channel">ช่องทาง *</label>
              <select id="channel" value={form.channel} onChange={(e) => set('channel', e.target.value)}>
                {DONATION_CHANNELS.map((ch) => (
                  <option key={ch} value={ch}>
                    {DONATION_CHANNEL_LABEL_TH[ch]}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="bank">บัญชีปลายทาง</label>
              <select
                id="bank"
                value={form.bankAccountId}
                onChange={(e) => set('bankAccountId', e.target.value)}
              >
                <option value="">ไม่ระบุ</option>
                {banks.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.bankName} {b.accountNoMasked}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="camp">แคมเปญ</label>
              <select
                id="camp"
                value={form.campaignId}
                onChange={(e) => set('campaignId', e.target.value)}
              >
                <option value="">บริจาคทั่วไป</option>
                {campaigns.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="field">
              <label htmlFor="payer">ชื่อผู้โอน / ผู้ให้</label>
              <input id="payer" value={form.payerName} onChange={(e) => set('payerName', e.target.value)} />
            </div>
            <div className="field">
              <label htmlFor="ref">เลขอ้างอิงบนสลิป</label>
              <input id="ref" value={form.slipRef} onChange={(e) => set('slipRef', e.target.value)} />
              <div className="hint">กรอกไว้ช่วยกันบันทึกซ้ำในอนาคต</div>
            </div>
          </div>

          <div className="field">
            <label htmlFor="note">บันทึกเพิ่มเติม</label>
            <input id="note" value={form.note} onChange={(e) => set('note', e.target.value)} />
          </div>
        </div>

        <div className="card">
          <h2>ผู้บริจาค</h2>
          {donor ? (
            <div className="btn-row" style={{ alignItems: 'center' }}>
              <span>
                <strong>{donor.displayName}</strong>{' '}
                <span className="muted small mono">{donor.donorCode}</span>
              </span>
              <button type="button" className="link" onClick={() => setDonor(null)}>
                เปลี่ยน
              </button>
            </div>
          ) : (
            <>
              <div className="field">
                <label htmlFor="dq">ค้นหาผู้บริจาคเดิม</label>
                <input
                  id="dq"
                  type="search"
                  value={donorQuery}
                  onChange={(e) => setDonorQuery(e.target.value)}
                  placeholder="ชื่อ เบอร์โทร หรือรหัสผู้บริจาค"
                />
                <div className="hint">
                  ไม่ระบุ = บันทึกเป็นไม่ประสงค์ออกนาม · จับคู่ภายหลังตอนยืนยันได้
                </div>
              </div>
              {donorResults.length > 0 && (
                <div className="table-wrap">
                  <table>
                    <tbody>
                      {donorResults.map((r) => (
                        <tr key={r.id}>
                          <td className="wrap">{r.displayName}</td>
                          <td className="small">{r.phone ?? '—'}</td>
                          <td>
                            <button type="button" className="link" onClick={() => setDonor(r)}>
                              เลือก
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="card">
          <div className="btn-row">
            <button type="submit" disabled={busy}>
              {busy ? 'กำลังบันทึก…' : 'บันทึกรายการ'}
            </button>
            <Link className="btn secondary" to="/donations">
              ยกเลิก
            </Link>
          </div>
        </div>
      </form>
    </>
  );
}
